import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import type { AppNotification } from '../../types/Notification';
import {
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsRead as markAllNotificationsReadRequest,
} from '../../services/notifications';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

type NotificationsState = {
  unreadCount: number;
  items: AppNotification[];
  listStatus: Status;
  countStatus: Status;
  latestCountRequestId: string | null;
  latestListRequestId: string | null;
};

const initialState: NotificationsState = {
  unreadCount: 0,
  items: [],
  listStatus: 'idle',
  countStatus: 'idle',
  latestCountRequestId: null,
  latestListRequestId: null,
};

export const fetchUnreadCount = createAsyncThunk<number>(
  'notifications/fetchUnreadCount',
  getUnreadNotificationCount
);

export const fetchNotifications = createAsyncThunk<AppNotification[]>(
  'notifications/fetchNotifications',
  getNotifications
);

export const markAllNotificationsRead = createAsyncThunk<number>(
  'notifications/markAllRead',
  markAllNotificationsReadRequest
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnreadCount.pending, (state, action) => {
        state.countStatus = 'loading';
        state.latestCountRequestId = action.meta.requestId;
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        if (action.meta.requestId === state.latestCountRequestId) {
          state.unreadCount = action.payload;
          state.countStatus = 'succeeded';
        }
      })
      .addCase(fetchUnreadCount.rejected, (state, action) => {
        if (action.meta.requestId === state.latestCountRequestId) {
          state.countStatus = 'failed';
        }
      })
      .addCase(fetchNotifications.pending, (state, action) => {
        state.listStatus = 'loading';
        state.latestListRequestId = action.meta.requestId;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        if (action.meta.requestId === state.latestListRequestId) {
          state.items = action.payload;
          state.listStatus = 'succeeded';
        }
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        if (action.meta.requestId === state.latestListRequestId) {
          state.listStatus = 'failed';
        }
      })
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.unreadCount = 0;
        state.latestCountRequestId = null;

        state.items.forEach((item) => {
          if (item.readAt === null) item.readAt = new Date().toISOString();
        });
      });
  },
});

export const selectNotificationUnreadCount = (state: RootState) =>
  state.notifications.unreadCount;
export const selectNotifications = (state: RootState) =>
  state.notifications.items;

export default notificationsSlice.reducer;
